/**
 * Server-state da Plataforma via React Query (D-10.3, Task 10).
 *
 * Um único dataset alimenta dashboards e drill-downs: o card e a lista leem a
 * MESMA entrada de cache (`DASHBOARD_DATASET_QUERY_KEY`), então não existe
 * janela em que o número e a lista discordem por terem buscado em momentos
 * diferentes.
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult
} from "@tanstack/react-query";
import type { NewStaffUserInput, StaffUser, StaffUserEvent, StaffUserPatch } from "./users/model.js";
import type { PlatformApiClient } from "./api/platform-client.js";
import type { ChatOperators } from "./api/ai-types.js";
import type { InboxStats } from "../../../packages/integration-wapi/src/stats.js";
import type { AiDraft, AiEnrichment, AiEnrichmentDraft, AiEnrichmentTarget, AiMemoryItem, AiOverview, AiSecretStatus, AiSettings, AiStatus, AiTestResult } from "./api/ai-types.js";
import { useApiClient } from "./api/ApiClientContext.js";
import type { DashboardDataset } from "./dashboard/kpis.js";
import type { CatalogDataset } from "./catalog/dataset.js";
import type { OrdersDataset } from "./orders/dataset.js";
import type { BranchStockSnapshot } from "./catalog/branch-stock.js";
import type { FinanceAction, FinanceSettings, NewPayableInput, NewReceivableInput, Payable, Receivable, SettleInput } from "./finance/types.js";
import type { MarketplaceListing, PaymentLink, ProductApplication } from "./contracts/platform-extras.js";
import type { OrderTransitionInput, PlatformOrderState } from "./orders/state-machine.js";
import type { FulfillmentDataset } from "./fulfillment/dataset.js";
import type { FulfillmentStage, FulfillmentStageInput, OccurrenceKind } from "./fulfillment/state-machine.js";
import type { RmaDataset } from "./rma/dataset.js";
import type { RmaState, RmaTransitionInput } from "./rma/state-machine.js";
import type { FinancialDataset } from "./financial/dataset.js";
import type { CrmDataset } from "./crm/dataset.js";
import type { Crm360Payload } from "./crm/contract.js";
import type { LeadStage, StageTransitionInput } from "./crm/pipeline.js";
import type { CommercialLayerPatch, CreatePlatformOrderInput, MediaCreatePatch, MediaUpdatePatch, OrderAcquisitionInput } from "./api/platform-client.js";
import type { SalesOriginOption } from "./orders/acquisition.js";
import type {
  Banner,
  Coupon,
  CouponDiscountType,
  BlingReprocessResult,
  CrmOpportunity,
  CrmTask,
  FulfillmentTask,
  MarketingFeatureFlag,
  MarketingFeatureKey,
  PlatformAuditEvent,
  PlatformCatalogProduct,
  PlatformConversation,
  QuickReply,
  QuickReplyInput,
  SavedFlow,
  SavedFlowInput,
  WhatsAppGroup,
  PlatformRmaCase,
  PlatformUnifiedOrder,
  OrdersSyncResult,
  OrdersSyncState,
  PlatformIntegrationStatus,
  PresaleHighlight
} from "./api/types.js";
import type { OrdersRange } from "./period.js";

export const DASHBOARD_DATASET_QUERY_KEY = ["plataforma", "dashboard-dataset"] as const;
export const CATALOG_DATASET_QUERY_KEY = ["plataforma", "catalog-dataset"] as const;
export const ORDERS_DATASET_QUERY_KEY = ["plataforma", "orders-dataset"] as const;
export const CRM_DATASET_QUERY_KEY = ["plataforma", "crm-dataset"] as const;
export const CRM_360_QUERY_KEY = ["plataforma", "crm-360"] as const;
export const FULFILLMENT_DATASET_QUERY_KEY = ["plataforma", "fulfillment-dataset"] as const;
export const RMA_DATASET_QUERY_KEY = ["plataforma", "rma-dataset"] as const;
export const FINANCIAL_DATASET_QUERY_KEY = ["plataforma", "financial-dataset"] as const;
export const AUDIT_TRAIL_QUERY_KEY = ["plataforma", "audit-trail"] as const;
export const INTEGRATIONS_QUERY_KEY = ["plataforma", "config-integrations"] as const;
export const MARKETING_FEATURE_FLAGS_QUERY_KEY = ["plataforma", "marketing-feature-flags"] as const;
export const PRESALE_HIGHLIGHTS_QUERY_KEY = ["plataforma", "presale-highlights"] as const;
export const BANNERS_QUERY_KEY = ["plataforma", "banners"] as const;
export const COUPONS_QUERY_KEY = ["plataforma", "coupons"] as const;

/** Intervalo de atualização do painel operacional (ms). */
export const DASHBOARD_REFETCH_INTERVAL_MS = 30_000;

export function useDashboardDataset(): UseQueryResult<DashboardDataset, Error> {
  const client = useApiClient();
  return useQuery<DashboardDataset, Error>({
    queryKey: DASHBOARD_DATASET_QUERY_KEY,
    queryFn: () => client.loadDashboardDataset(),
    staleTime: 10_000,
    refetchInterval: DASHBOARD_REFETCH_INTERVAL_MS
  });
}

/** Intervalo de atualização da conversa aberta (ms): atendimento precisa parecer ao vivo. */
export const CHANNEL_CONVERSATION_REFETCH_INTERVAL_MS = 4_000;
export const channelConversationQueryKey = (id: string) => ["plataforma", "channel-conversation", id] as const;

/** Conversa individual com polling curto, separada do snapshot do dashboard. */
export function useChannelConversation(id: string): UseQueryResult<PlatformConversation, Error> {
  const client = useApiClient();
  return useQuery<PlatformConversation, Error>({
    queryKey: channelConversationQueryKey(id),
    queryFn: () => client.getChannelConversation(id),
    // Sem conversa aberta (`/inbox`) o id é vazio: não pede `/api/channels/` (404 inútil no console).
    enabled: Boolean(id),
    refetchInterval: CHANNEL_CONVERSATION_REFETCH_INTERVAL_MS
  });
}

/** Reação e leitura da conversa aberta. A conversa é recarregada em seguida (reações vêm no DTO). */
export function useChatActions(conversationId: string) {
  const client = useApiClient();
  const queryClient = useQueryClient();
  const reload = () => queryClient.invalidateQueries({ queryKey: channelConversationQueryKey(conversationId) });
  return {
    react: useMutation<void, Error, { messageId: string; emoji: string }>({ mutationFn: ({ messageId, emoji }) => client.reactToMessage(conversationId, messageId, emoji), onSuccess: reload }),
    // Ao concluir, recarrega a conversa: o texto passa a vir no DTO e some o botão "Transcrever".
    transcribe: useMutation<string, Error, { messageId: string }>({ mutationFn: ({ messageId }) => client.transcribeMessage(conversationId, messageId), onSuccess: reload }),
    markRead: useMutation<void, Error, void>({ mutationFn: () => client.markConversationRead(conversationId), onSuccess: () => { void queryClient.invalidateQueries({ queryKey: DASHBOARD_DATASET_QUERY_KEY }); } })
  };
}

// ---- Assistente de IA ------------------------------------------------------------------------------------------
const AI_KEY = ["plataforma", "ai"] as const;
export function useAiStatus(): UseQueryResult<AiStatus, Error> {
  const client = useApiClient();
  return useQuery<AiStatus, Error>({ queryKey: [...AI_KEY, "status"], queryFn: () => client.getAiStatus(), staleTime: 15_000, retry: false });
}
export function useAiDrafts(conversationId: string): UseQueryResult<AiDraft[], Error> {
  const client = useApiClient();
  return useQuery<AiDraft[], Error>({ queryKey: [...AI_KEY, "drafts", conversationId], queryFn: () => client.listAiDrafts(conversationId), refetchInterval: 5_000, enabled: Boolean(conversationId), retry: false });
}
export function useAiDraftAction(conversationId: string) {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation<void, Error, { id: string; action: "approve" | "discard"; text?: string }>({
    mutationFn: ({ id, action, text }) => client.resolveAiDraft(id, action, text),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [...AI_KEY, "drafts", conversationId] });
      void queryClient.invalidateQueries({ queryKey: channelConversationQueryKey(conversationId) });
    }
  });
}
export function useAiOverview(enabled = true): UseQueryResult<AiOverview, Error> {
  const client = useApiClient();
  return useQuery<AiOverview, Error>({ queryKey: [...AI_KEY, "overview"], queryFn: () => client.getAiOverview(), refetchInterval: 30_000, enabled, retry: false });
}
export function useAiSettings(enabled = true): UseQueryResult<AiSettings, Error> {
  const client = useApiClient();
  return useQuery<AiSettings, Error>({ queryKey: [...AI_KEY, "settings"], queryFn: () => client.getAiSettings(), enabled, retry: false, staleTime: 5_000 });
}
export function useAiSettingsSave() {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation<AiSettings, Error, Record<string, unknown>>({
    mutationFn: (patch) => client.saveAiSettings(patch),
    onSuccess: (saved) => {
      queryClient.setQueryData([...AI_KEY, "settings"], saved);
      void queryClient.invalidateQueries({ queryKey: [...AI_KEY, "status"] });
      void queryClient.invalidateQueries({ queryKey: [...AI_KEY, "overview"] });
    }
  });
}
// IA do catálogo: rascunhos de enriquecimento (site / marketplace) de um produto.
export function useProductEnrichments(productId: string): UseQueryResult<AiEnrichment[], Error> {
  const client = useApiClient();
  return useQuery<AiEnrichment[], Error>({ queryKey: [...AI_KEY, "enrichments", productId], queryFn: () => client.listProductEnrichments(productId), enabled: Boolean(productId), retry: false });
}
export function useProductEnrichmentActions(productId: string) {
  const client = useApiClient();
  const queryClient = useQueryClient();
  const reload = () => { void queryClient.invalidateQueries({ queryKey: [...AI_KEY, "enrichments", productId] }); };
  return {
    generate: useMutation<unknown, Error, { target: AiEnrichmentTarget; guidance: string }>({ mutationFn: ({ target, guidance }) => client.enrichProductWithAi(productId, target, guidance), onSuccess: reload }),
    resolve: useMutation<unknown, Error, { id: string; action: "approve" | "discard"; edited?: Partial<AiEnrichmentDraft> }>({
      mutationFn: ({ id, action, edited }) => client.resolveProductEnrichment(id, action, edited),
      // Aprovar "site" grava na camada comercial: recarrega o catálogo para a tela mostrar o texto novo.
      onSuccess: () => { reload(); void queryClient.invalidateQueries({ queryKey: CATALOG_DATASET_QUERY_KEY }); }
    })
  };
}
export function useAiSecrets(enabled = true): UseQueryResult<AiSecretStatus[], Error> {
  const client = useApiClient();
  return useQuery<AiSecretStatus[], Error>({ queryKey: [...AI_KEY, "secrets"], queryFn: () => client.listAiSecrets(), enabled, retry: false });
}
export function useAiSecretActions() {
  const client = useApiClient();
  const queryClient = useQueryClient();
  const reload = () => {
    void queryClient.invalidateQueries({ queryKey: [...AI_KEY, "secrets"] });
    void queryClient.invalidateQueries({ queryKey: [...AI_KEY, "overview"] });
  };
  return {
    save: useMutation<AiSecretStatus, Error, { name: string; value: string }>({ mutationFn: ({ name, value }) => client.saveAiSecret(name, value), onSuccess: reload }),
    remove: useMutation<void, Error, string>({ mutationFn: (name) => client.removeAiSecret(name), onSuccess: reload }),
    test: useMutation<{ ok: boolean; detail: string }, Error, string>({ mutationFn: (name) => client.testAiSecret(name) })
  };
}
export function useAiMemory(status?: string, enabled = true): UseQueryResult<AiMemoryItem[], Error> {
  const client = useApiClient();
  return useQuery<AiMemoryItem[], Error>({ queryKey: [...AI_KEY, "memory", status ?? "all"], queryFn: () => client.listAiMemory(status), enabled, retry: false });
}
export function useAiMemoryCurate() {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation<void, Error, { id: string; patch: { status?: string; answer?: string } }>({
    mutationFn: ({ id, patch }) => client.curateAiMemory(id, patch),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [...AI_KEY, "memory"] });
      void queryClient.invalidateQueries({ queryKey: [...AI_KEY, "overview"] });
    }
  });
}
export function useAiTest() {
  const client = useApiClient();
  return useMutation<AiTestResult, Error, string>({ mutationFn: (text) => client.testAi(text) });
}

export function useWhatsAppGroups(): UseQueryResult<WhatsAppGroup[], Error> {
  const client = useApiClient();
  return useQuery<WhatsAppGroup[], Error>({ queryKey: ["plataforma", "whatsapp-groups"], queryFn: () => client.listWhatsAppGroups(), staleTime: 60_000 });
}

export function useWhatsAppGroupSend() {
  const client = useApiClient();
  return useMutation<{ groupId: string; sentAt: string }, Error, { groupId: string; text: string }>({
    mutationFn: ({ groupId, text }) => client.sendToWhatsAppGroup(groupId, text)
  });
}

export const CONVERSATION_TAGS_QUERY_KEY = ["plataforma", "conversation-tags"] as const;

/** Mapa id da conversa/grupo → tags. `null` = rota de tags não publicada neste ambiente. */
export function useConversationTags(): UseQueryResult<Record<string, string[]> | null, Error> {
  const client = useApiClient();
  return useQuery<Record<string, string[]> | null, Error>({ queryKey: CONVERSATION_TAGS_QUERY_KEY, queryFn: () => client.getConversationTags(), staleTime: 15_000 });
}

export function useSetConversationTags() {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation<string[], Error, { id: string; tags: string[] }>({
    mutationFn: ({ id, tags }) => client.setConversationTags(id, tags),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: CONVERSATION_TAGS_QUERY_KEY })
  });
}

export const FLOWS_QUERY_KEY = ["plataforma", "channel-flows"] as const;

export function useFlows(): UseQueryResult<SavedFlow[], Error> {
  const client = useApiClient();
  return useQuery<SavedFlow[], Error>({ queryKey: FLOWS_QUERY_KEY, queryFn: () => client.listFlows(), staleTime: 30_000 });
}

export function useFlowActions() {
  const client = useApiClient();
  const queryClient = useQueryClient();
  const onSuccess = () => { void queryClient.invalidateQueries({ queryKey: FLOWS_QUERY_KEY }); };
  return {
    save: useMutation<SavedFlow, Error, { input: SavedFlowInput; id?: string }>({ mutationFn: ({ input, id }) => client.saveFlow(input, id), onSuccess }),
    remove: useMutation<void, Error, string>({ mutationFn: (id) => client.deleteFlow(id), onSuccess })
  };
}

export const QUICK_REPLIES_QUERY_KEY = ["plataforma", "quick-replies"] as const;

export function useQuickReplies(): UseQueryResult<QuickReply[], Error> {
  const client = useApiClient();
  return useQuery<QuickReply[], Error>({ queryKey: QUICK_REPLIES_QUERY_KEY, queryFn: () => client.listQuickReplies(), staleTime: 30_000 });
}

export function useQuickReplyActions() {
  const client = useApiClient();
  const queryClient = useQueryClient();
  const onSuccess = () => { void queryClient.invalidateQueries({ queryKey: QUICK_REPLIES_QUERY_KEY }); };
  return {
    create: useMutation<QuickReply, Error, QuickReplyInput>({ mutationFn: (input) => client.createQuickReply(input), onSuccess }),
    update: useMutation<QuickReply, Error, { id: string; patch: Partial<QuickReplyInput> }>({ mutationFn: ({ id, patch }) => client.updateQuickReply(id, patch), onSuccess }),
    remove: useMutation<void, Error, string>({ mutationFn: (id) => client.deleteQuickReply(id), onSuccess })
  };
}

/** Enviar resposta humana / assumir / devolver à IA. Todas atualizam a conversa e o snapshot. */
export function useChannelConversationActions(id: string) {
  const client = useApiClient();
  const queryClient = useQueryClient();
  const refresh = (conversation: PlatformConversation) => {
    queryClient.setQueryData(channelConversationQueryKey(id), conversation);
    void queryClient.invalidateQueries({ queryKey: DASHBOARD_DATASET_QUERY_KEY });
  };
  return {
    send: useMutation<{ conversation: PlatformConversation; dispatch: "sent" | "queued" }, Error, string | { text: string; quotedMessageId?: string }>({
      mutationFn: (input) => typeof input === "string" ? client.sendChannelMessage(id, input) : client.sendChannelMessage(id, input.text, input.quotedMessageId),
      onSuccess: ({ conversation }) => refresh(conversation)
    }),
    sendMedia: useMutation<
      { conversation: PlatformConversation; dispatch: "sent" | "queued" },
      Error,
      Parameters<typeof client.sendChannelMedia>[1]
    >({
      mutationFn: (media) => client.sendChannelMedia(id, media),
      onSuccess: ({ conversation }) => refresh(conversation)
    }),
    setMode: useMutation<PlatformConversation, Error, "takeover" | "resume">({
      mutationFn: (action) => client.setChannelConversationMode(id, action),
      onSuccess: refresh
    }),
    /** Transferir (ou liberar com `null`). Também atualiza o selo de responsável da lista. */
    assign: useMutation<PlatformConversation, Error, string | null>({
      mutationFn: (assigneeId) => client.assignChannelConversation(id, assigneeId),
      onSuccess: (conversation) => { refresh(conversation); void queryClient.invalidateQueries({ queryKey: INBOX_PULSE_QUERY_KEY }); }
    })
  };
}

export const INBOX_PULSE_QUERY_KEY = ["plataforma", "inbox-pulse"] as const;
/** Intervalo da lista de conversas do atendimento (ms): curto de propósito, é de onde saem os alertas de mensagem nova. */
export const INBOX_PULSE_INTERVAL_MS = 5_000;

/** Lista de conversas com polling curto (o dataset do dashboard só atualiza a cada 30 s). `undefined` = ainda não carregou. */
export function useInboxPulse(enabled = true): UseQueryResult<PlatformConversation[], Error> {
  const client = useApiClient();
  return useQuery<PlatformConversation[], Error>({
    queryKey: INBOX_PULSE_QUERY_KEY,
    queryFn: () => client.listConversations(),
    refetchInterval: INBOX_PULSE_INTERVAL_MS,
    enabled,
    retry: false
  });
}

/** Operadores (para transferir) e identidade do usuário logado. `retry: false` = rota ausente só desliga o recurso. */
export function useChatOperators(): UseQueryResult<ChatOperators, Error> {
  const client = useApiClient();
  return useQuery<ChatOperators, Error>({ queryKey: ["plataforma", "chat-operators"], queryFn: () => client.getChannelOperators(), staleTime: 60_000, retry: false });
}

/** Triagem por IA das conversas que esperam a equipe. Erro (sem chave, teto diário) só desliga o recurso. */
export function useInboxTriage() {
  const client = useApiClient();
  return useMutation<Array<{ id: string; urgency: number; reason: string }>, Error, Array<{ id: string; name: string; waitingMinutes: number; unassigned: boolean }>>({ mutationFn: (items) => client.triageConversations(items) });
}

/** Estatísticas gerais de atendimento (tempo de resposta), atualizadas a cada minuto. */
export function useInboxStats(): UseQueryResult<InboxStats, Error> {
  const client = useApiClient();
  return useQuery<InboxStats, Error>({ queryKey: ["plataforma", "inbox-stats"], queryFn: () => client.getInboxStats(), refetchInterval: 60_000, staleTime: 30_000, retry: false });
}

/**
 * Catálogo/estoque/divergências (Task 11). Mesmo princípio do dataset do
 * dashboard: lista de produtos, detalhe do produto, disponibilidade e
 * divergências leem a MESMA entrada de cache, então a contagem de divergências
 * de um produto na lista nunca discorda da tela dele.
 */
export function useApplications(): UseQueryResult<ProductApplication[] | null, Error> {
  const client = useApiClient();
  return useQuery<ProductApplication[] | null, Error>({ queryKey: ["plataforma", "applications"], queryFn: () => client.loadApplications(), staleTime: 10_000 });
}

export function useApplicationActions() {
  const client = useApiClient();
  const queryClient = useQueryClient();
  const refresh = () => queryClient.invalidateQueries({ queryKey: ["plataforma", "applications"] });
  return {
    add: useMutation<ProductApplication, Error, Parameters<typeof client.addApplication>[0]>({ mutationFn: (input) => client.addApplication(input), onSuccess: refresh }),
    remove: useMutation<void, Error, string>({ mutationFn: (id) => client.deleteApplication(id), onSuccess: refresh })
  };
}

export function useSaveBranchBalance() {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation<void, Error, { branchId: string; productId: string; balance: number }>({
    mutationFn: (input) => client.saveBranchBalance(input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["plataforma", "branch-stock"] })
  });
}

export function usePaymentLinks(platformOrderId: string | null): UseQueryResult<PaymentLink[], Error> {
  const client = useApiClient();
  return useQuery<PaymentLink[], Error>({
    queryKey: ["plataforma", "payment-links", platformOrderId],
    queryFn: () => client.listPaymentLinks(platformOrderId ?? ""),
    enabled: platformOrderId !== null
  });
}

export function useCreatePaymentLink(platformOrderId: string) {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation<PaymentLink, Error, void>({
    mutationFn: () => client.createPaymentLink(platformOrderId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["plataforma", "payment-links", platformOrderId] })
  });
}

export function useMarketplaceListings(): UseQueryResult<MarketplaceListing[] | null, Error> {
  const client = useApiClient();
  return useQuery<MarketplaceListing[] | null, Error>({ queryKey: ["plataforma", "marketplace-listings"], queryFn: () => client.loadMarketplaceListings(), staleTime: 10_000 });
}

const FINANCE_KEY = ["plataforma", "finance"] as const;

export function usePayables(): UseQueryResult<Payable[] | null, Error> {
  const client = useApiClient();
  return useQuery<Payable[] | null, Error>({ queryKey: [...FINANCE_KEY, "payables"], queryFn: () => client.loadPayables(), staleTime: 10_000 });
}

export function useReceivables(): UseQueryResult<Receivable[] | null, Error> {
  const client = useApiClient();
  return useQuery<Receivable[] | null, Error>({ queryKey: [...FINANCE_KEY, "receivables"], queryFn: () => client.loadReceivables(), staleTime: 10_000 });
}

export function useFinanceSettings(): UseQueryResult<FinanceSettings | null, Error> {
  const client = useApiClient();
  return useQuery<FinanceSettings | null, Error>({ queryKey: [...FINANCE_KEY, "settings"], queryFn: () => client.loadFinanceSettings(), staleTime: 10_000 });
}

type FinanceUpdate = SettleInput | FinanceAction | Record<string, unknown>;

/** Toda escrita do financeiro invalida as três listas: o fluxo de caixa depende de todas. */
export function useFinanceMutations() {
  const client = useApiClient();
  const queryClient = useQueryClient();
  const onSuccess = () => queryClient.invalidateQueries({ queryKey: FINANCE_KEY });
  return {
    createPayable: useMutation<Payable[], Error, NewPayableInput>({ mutationFn: (input) => client.createPayable(input), onSuccess }),
    updatePayable: useMutation<Payable, Error, { id: string; input: FinanceUpdate }>({ mutationFn: ({ id, input }) => client.updatePayable(id, input), onSuccess }),
    createReceivable: useMutation<Receivable[], Error, NewReceivableInput>({ mutationFn: (input) => client.createReceivable(input), onSuccess }),
    updateReceivable: useMutation<Receivable, Error, { id: string; input: FinanceUpdate }>({ mutationFn: ({ id, input }) => client.updateReceivable(id, input), onSuccess }),
    saveSettings: useMutation<FinanceSettings, Error, FinanceSettings>({ mutationFn: (input) => client.saveFinanceSettings(input), onSuccess })
  };
}

export function useBranchStock(): UseQueryResult<BranchStockSnapshot | null, Error> {
  const client = useApiClient();
  return useQuery<BranchStockSnapshot | null, Error>({
    queryKey: ["plataforma", "branch-stock"],
    queryFn: () => client.loadBranchStock(),
    staleTime: 10_000
  });
}

export function useCatalogDataset(): UseQueryResult<CatalogDataset, Error> {
  const client = useApiClient();
  return useQuery<CatalogDataset, Error>({
    queryKey: CATALOG_DATASET_QUERY_KEY,
    queryFn: () => client.loadCatalogDataset(),
    staleTime: 10_000
  });
}

/**
 * Edição comercial do produto (Task 11). Invalida o dataset de catálogo no
 * sucesso: a lista, o detalhe e as divergências voltam a ler do servidor em vez
 * de confiar num estado local que poderia divergir do banco.
 */
export function useCommercialLayerUpdate(): UseMutationResult<
  PlatformCatalogProduct,
  Error,
  { productId: string; patch: CommercialLayerPatch }
> {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation<PlatformCatalogProduct, Error, { productId: string; patch: CommercialLayerPatch }>({
    mutationFn: ({ productId, patch }) => client.updateCommercialLayer(productId, patch),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: CATALOG_DATASET_QUERY_KEY });
      // O dashboard lê reservas/estoque do mesmo catálogo: invalida também.
      void queryClient.invalidateQueries({ queryKey: DASHBOARD_DATASET_QUERY_KEY });
    }
  });
}

/**
 * Mídia do produto (Plano Loja/Twotrees, Fase 2) — mesma invalidação da edição
 * comercial: a Loja depende do MESMO dataset de catálogo para Hero/vitrine/PDP/
 * mega menu (Fases 3-5), então uma mídia nova precisa refletir em toda tela
 * admin que lê o produto, não só nesta.
 */
export function useProductMediaAdd(): UseMutationResult<PlatformCatalogProduct, Error, { productId: string; patch: MediaCreatePatch }> {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation<PlatformCatalogProduct, Error, { productId: string; patch: MediaCreatePatch }>({
    mutationFn: ({ productId, patch }) => client.addProductMedia(productId, patch),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: CATALOG_DATASET_QUERY_KEY });
      void queryClient.invalidateQueries({ queryKey: DASHBOARD_DATASET_QUERY_KEY });
    }
  });
}

export function useProductMediaUpdate(): UseMutationResult<
  PlatformCatalogProduct,
  Error,
  { productId: string; mediaId: string; patch: MediaUpdatePatch }
> {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation<PlatformCatalogProduct, Error, { productId: string; mediaId: string; patch: MediaUpdatePatch }>({
    mutationFn: ({ productId, mediaId, patch }) => client.updateProductMedia(productId, mediaId, patch),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: CATALOG_DATASET_QUERY_KEY });
      void queryClient.invalidateQueries({ queryKey: DASHBOARD_DATASET_QUERY_KEY });
    }
  });
}

export function useProductMediaRemove(): UseMutationResult<PlatformCatalogProduct, Error, { productId: string; mediaId: string }> {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation<PlatformCatalogProduct, Error, { productId: string; mediaId: string }>({
    mutationFn: ({ productId, mediaId }) => client.removeProductMedia(productId, mediaId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: CATALOG_DATASET_QUERY_KEY });
      void queryClient.invalidateQueries({ queryKey: DASHBOARD_DATASET_QUERY_KEY });
    }
  });
}

/**
 * Marketing da Loja (Plano Loja/Twotrees, Fase 7/8) — feature flags +
 * destaques da faixa de pré-venda. Duas entradas de cache próprias: a lista
 * de flags muda raramente, os destaques mudam mais — invalidar uma não
 * precisa invalidar a outra.
 */
export function useMarketingFeatureFlags(): UseQueryResult<MarketingFeatureFlag[], Error> {
  const client = useApiClient();
  return useQuery<MarketingFeatureFlag[], Error>({
    queryKey: MARKETING_FEATURE_FLAGS_QUERY_KEY,
    queryFn: () => client.listMarketingFeatureFlags(),
    staleTime: 10_000
  });
}

export function useSetMarketingFeatureFlag(): UseMutationResult<MarketingFeatureFlag, Error, { featureKey: MarketingFeatureKey; enabled: boolean }> {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation<MarketingFeatureFlag, Error, { featureKey: MarketingFeatureKey; enabled: boolean }>({
    mutationFn: ({ featureKey, enabled }) => client.setMarketingFeatureFlag(featureKey, enabled),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: MARKETING_FEATURE_FLAGS_QUERY_KEY })
  });
}

export function usePresaleHighlights(): UseQueryResult<PresaleHighlight[], Error> {
  const client = useApiClient();
  return useQuery<PresaleHighlight[], Error>({
    queryKey: PRESALE_HIGHLIGHTS_QUERY_KEY,
    queryFn: () => client.listPresaleHighlights(),
    staleTime: 10_000
  });
}

export function useAddPresaleHighlight(): UseMutationResult<
  PresaleHighlight,
  Error,
  { productId: string; headline: string; active?: boolean; position?: number; startsAt?: string | null; endsAt?: string | null }
> {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { productId: string; headline: string; active?: boolean; position?: number; startsAt?: string | null; endsAt?: string | null }) =>
      client.addPresaleHighlight(input),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: PRESALE_HIGHLIGHTS_QUERY_KEY })
  });
}

export function useUpdatePresaleHighlight(): UseMutationResult<
  PresaleHighlight,
  Error,
  { id: string; patch: { headline?: string; active?: boolean; position?: number; startsAt?: string | null; endsAt?: string | null } }
> {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: { headline?: string; active?: boolean; position?: number; startsAt?: string | null; endsAt?: string | null } }) =>
      client.updatePresaleHighlight(id, patch),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: PRESALE_HIGHLIGHTS_QUERY_KEY })
  });
}

export function useRemovePresaleHighlight(): UseMutationResult<void, Error, { id: string }> {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: { id: string }) => client.removePresaleHighlight(id),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: PRESALE_HIGHLIGHTS_QUERY_KEY })
  });
}

/**
 * Plano Loja/Twotrees, Fase 9 — banners promocionais da home. Mesmo padrão
 * de cache dos destaques da faixa de pré-venda acima.
 */
export function useBanners(): UseQueryResult<Banner[], Error> {
  const client = useApiClient();
  return useQuery<Banner[], Error>({
    queryKey: BANNERS_QUERY_KEY,
    queryFn: () => client.listBanners(),
    staleTime: 10_000
  });
}

export function useAddBanner(): UseMutationResult<
  Banner,
  Error,
  { imageUrl: string; altText: string; linkUrl?: string | null; active?: boolean; position?: number; startsAt?: string | null; endsAt?: string | null }
> {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { imageUrl: string; altText: string; linkUrl?: string | null; active?: boolean; position?: number; startsAt?: string | null; endsAt?: string | null }) =>
      client.addBanner(input),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: BANNERS_QUERY_KEY })
  });
}

export function useUpdateBanner(): UseMutationResult<
  Banner,
  Error,
  { id: string; patch: { imageUrl?: string; altText?: string; linkUrl?: string | null; active?: boolean; position?: number; startsAt?: string | null; endsAt?: string | null } }
> {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: { imageUrl?: string; altText?: string; linkUrl?: string | null; active?: boolean; position?: number; startsAt?: string | null; endsAt?: string | null } }) =>
      client.updateBanner(id, patch),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: BANNERS_QUERY_KEY })
  });
}

export function useRemoveBanner(): UseMutationResult<void, Error, { id: string }> {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: { id: string }) => client.removeBanner(id),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: BANNERS_QUERY_KEY })
  });
}

/** Plano Loja/Twotrees, Fase 10 — cupons de desconto. Sem hook de remoção: o contrato não tem DELETE. */
export function useCoupons(): UseQueryResult<Coupon[], Error> {
  const client = useApiClient();
  return useQuery<Coupon[], Error>({
    queryKey: COUPONS_QUERY_KEY,
    queryFn: () => client.listCoupons(),
    staleTime: 10_000
  });
}

type CouponCreateInput = {
  code: string;
  discountType: CouponDiscountType;
  discountValue: number;
  active?: boolean;
  startsAt?: string | null;
  endsAt?: string | null;
  minSubtotalCents?: number | null;
  maxUses?: number | null;
  maxUsesPerCustomer?: number | null;
};

type CouponPatch = Partial<Omit<CouponCreateInput, "code">>;

export function useAddCoupon(): UseMutationResult<Coupon, Error, CouponCreateInput> {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CouponCreateInput) => client.addCoupon(input),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: COUPONS_QUERY_KEY })
  });
}

export function useUpdateCoupon(): UseMutationResult<Coupon, Error, { id: string; patch: CouponPatch }> {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: CouponPatch }) => client.updateCoupon(id, patch),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: COUPONS_QUERY_KEY })
  });
}

/**
 * Reprocessamento de uma divergência (Task 17/R3): re-enfileira um sync do
 * Bling para a entidade divergente. No sucesso, invalida o dataset de catálogo
 * (a divergência pode sumir quando o sync corrigir o campo defasado) e o
 * dashboard (que conta falhas de sincronização). A tela só dispara a ação; a
 * verdade continua vindo do servidor na próxima leitura.
 */
export function useReprocessDivergence(): UseMutationResult<
  BlingReprocessResult,
  Error,
  { entityType: string; entityId: string }
> {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation<BlingReprocessResult, Error, { entityType: string; entityId: string }>({
    mutationFn: ({ entityType, entityId }) => client.reprocessDivergence(entityType, entityId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: CATALOG_DATASET_QUERY_KEY });
      void queryClient.invalidateQueries({ queryKey: DASHBOARD_DATASET_QUERY_KEY });
    }
  });
}

/**
 * Pedidos unificados multicanal (Task 12). Lista e detalhe leem a MESMA
 * entrada de cache: a linha do tempo do pedido na tela de detalhe é a mesma
 * que a lista usou para calcular o estado atual.
 */
export function useOrdersDataset(range?: OrdersRange): UseQueryResult<OrdersDataset, Error> {
  const client = useApiClient();
  return useQuery<OrdersDataset, Error>({
    queryKey: [...ORDERS_DATASET_QUERY_KEY, range?.from ?? "all", range?.to ?? "all"],
    queryFn: () => client.loadOrdersDataset(range),
    staleTime: 10_000
  });
}

/** Um pedido com o dado completo do canal (detalhe). `null` = não encontrado. */
export function useOrder(id: string): UseQueryResult<PlatformUnifiedOrder | null, Error> {
  const client = useApiClient();
  return useQuery<PlatformUnifiedOrder | null, Error>({
    queryKey: [...ORDERS_DATASET_QUERY_KEY, "one", id],
    queryFn: () => client.loadPlatformOrder(id),
    enabled: id !== ""
  });
}

export const ORDERS_SYNC_QUERY_KEY = ["plataforma", "orders-sync"] as const;

/** Quando o Supabase foi atualizado a partir do Bling pela última vez. */
export function useOrdersSyncState(): UseQueryResult<OrdersSyncState | null, Error> {
  const client = useApiClient();
  return useQuery<OrdersSyncState | null, Error>({ queryKey: ORDERS_SYNC_QUERY_KEY, queryFn: () => client.loadOrdersSyncState(), staleTime: 30_000 });
}

/** "Atualizar agora": depois da carga, pedidos, indicadores e contas a receber são relidos do Supabase. */
export function useSyncOrdersNow(): UseMutationResult<OrdersSyncResult, Error, void> {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation<OrdersSyncResult, Error, void>({
    mutationFn: () => client.syncOrdersNow(),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ORDERS_DATASET_QUERY_KEY });
      void queryClient.invalidateQueries({ queryKey: DASHBOARD_DATASET_QUERY_KEY });
      void queryClient.invalidateQueries({ queryKey: ORDERS_SYNC_QUERY_KEY });
      void queryClient.invalidateQueries({ queryKey: ["plataforma", "finance"] });
    }
  });
}

/**
 * Registro de venda de canal (Televendas, redesign admin Fase 3). Invalida o
 * dataset de pedidos e o do dashboard: a venda nova entra nas filas e nos
 * indicadores a partir da resposta do servidor, nunca de estado local.
 */
export function useCreatePlatformOrder(): UseMutationResult<PlatformUnifiedOrder, Error, CreatePlatformOrderInput> {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation<PlatformUnifiedOrder, Error, CreatePlatformOrderInput>({
    mutationFn: (input) => client.createPlatformOrder(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ORDERS_DATASET_QUERY_KEY });
      void queryClient.invalidateQueries({ queryKey: DASHBOARD_DATASET_QUERY_KEY });
    }
  });
}

/** Origens de aquisição cadastradas (0062). */
export const SALES_ORIGINS_QUERY_KEY = ["sales-origins"] as const;

export function useSalesOrigins(): UseQueryResult<SalesOriginOption[], Error> {
  const client = useApiClient();
  return useQuery<SalesOriginOption[], Error>({ queryKey: SALES_ORIGINS_QUERY_KEY, queryFn: () => client.listSalesOrigins() });
}

export function useCreateSalesOrigin(): UseMutationResult<SalesOriginOption, Error, string> {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation<SalesOriginOption, Error, string>({
    mutationFn: (label) => client.createSalesOrigin(label),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: SALES_ORIGINS_QUERY_KEY })
  });
}

export function useUpdateSalesOrigin(): UseMutationResult<SalesOriginOption, Error, { slug: string; active?: boolean; label?: string }> {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation<SalesOriginOption, Error, { slug: string; active?: boolean; label?: string }>({
    mutationFn: ({ slug, ...patch }) => client.updateSalesOrigin(slug, patch),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: SALES_ORIGINS_QUERY_KEY })
  });
}

/** Conversas por origem no período (denominador da conversão). `null` = rota não publicada neste ambiente. */
export function useAcquisitionContacts(range: { from: string; to: string }): UseQueryResult<Record<string, number> | null, Error> {
  const client = useApiClient();
  return useQuery<Record<string, number> | null, Error>({
    queryKey: ["plataforma", "acquisition-contacts", range.from, range.to],
    queryFn: () => client.loadAcquisitionContacts(range)
  });
}

/** Marca a origem de um pedido existente; recarrega pedidos e dashboard (a origem muda os relatórios). */
export function useSetOrderAcquisition(): UseMutationResult<void, Error, { orderId: string; input: OrderAcquisitionInput }> {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation<void, Error, { orderId: string; input: OrderAcquisitionInput }>({
    mutationFn: ({ orderId, input }) => client.setOrderAcquisition(orderId, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ORDERS_DATASET_QUERY_KEY });
      void queryClient.invalidateQueries({ queryKey: DASHBOARD_DATASET_QUERY_KEY });
    }
  });
}

/**
 * Transição de estado do pedido (Task 12). Invalida o dataset de pedidos e o
 * do dashboard no sucesso: pedido que passou a `paid` muda indicador, e um
 * estado local otimista poderia discordar do histórico gravado no servidor.
 */
export function useOrderTransition(): UseMutationResult<
  PlatformUnifiedOrder,
  Error,
  { orderId: string; input: { to: PlatformOrderState } & OrderTransitionInput }
> {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation<PlatformUnifiedOrder, Error, { orderId: string; input: { to: PlatformOrderState } & OrderTransitionInput }>({
    mutationFn: ({ orderId, input }) => client.transitionPlatformOrder(orderId, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ORDERS_DATASET_QUERY_KEY });
      void queryClient.invalidateQueries({ queryKey: DASHBOARD_DATASET_QUERY_KEY });
    }
  });
}

/**
 * CRM 360 (Task 13). Mesmo princípio dos datasets anteriores: a visão 360 do
 * cliente, o pipeline, os orçamentos, as tarefas e os segmentos leem a MESMA
 * entrada de cache — o estágio que o funil mostra é o estágio que o 360 do
 * cliente mostra, sem janela em que as duas telas discordem.
 */
export function useCrmDataset(): UseQueryResult<CrmDataset, Error> {
  const client = useApiClient();
  return useQuery<CrmDataset, Error>({
    queryKey: CRM_DATASET_QUERY_KEY,
    queryFn: () => client.loadCrmDataset(),
    staleTime: 10_000
  });
}

/**
 * Visão 360 do cliente pelo runtime persistente (`/api/crm/360`, Task 13).
 *
 * Entrada de cache SEPARADA e por cliente, de propósito: este recorte é do
 * cliente canônico resolvido no servidor, enquanto `CRM_DATASET_QUERY_KEY` é a
 * fila completa das telas de funil/propostas/tarefas. Guardar os dois na mesma
 * chave faria a última tela aberta sobrescrever a outra.
 *
 * `data: null` = agregação canônica não respondeu neste ambiente (rota não
 * publicada, identidade sem vínculo comprovado ou credencial do runtime
 * ausente). A tela declara isso e cai nas listas — nunca exibe zero.
 */
export function useCrm360(selector: {
  customerId?: string;
  phone?: string;
  email?: string;
}): UseQueryResult<Crm360Payload | null, Error> {
  const client = useApiClient();
  const key = selector.customerId ?? selector.phone ?? selector.email ?? "";
  return useQuery<Crm360Payload | null, Error>({
    queryKey: [...CRM_360_QUERY_KEY, key],
    queryFn: () => client.loadCrm360(selector),
    enabled: key !== "",
    staleTime: 10_000
  });
}

/**
 * Mudança de estágio da oportunidade (Task 13). Invalida o CRM no sucesso: o
 * funil, a visão 360 e a fila de tarefas voltam a ler do servidor em vez de
 * confiar num estado local que poderia divergir do histórico gravado.
 */
export function useOpportunityStage(): UseMutationResult<
  CrmOpportunity,
  Error,
  { opportunityId: string; input: { to: LeadStage } & StageTransitionInput }
> {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation<CrmOpportunity, Error, { opportunityId: string; input: { to: LeadStage } & StageTransitionInput }>({
    mutationFn: ({ opportunityId, input }) => client.advanceOpportunityStage(opportunityId, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: CRM_DATASET_QUERY_KEY });
      // A visão 360 tem a sua própria entrada de cache: sem invalidar aqui, o
      // estágio novo apareceria no funil e o 360 do mesmo cliente continuaria
      // mostrando o antigo.
      void queryClient.invalidateQueries({ queryKey: CRM_360_QUERY_KEY });
    }
  });
}

/** Conclusão/cancelamento de tarefa com resultado (fila de follow-up). */
export function useCrmTaskClose(): UseMutationResult<
  CrmTask,
  Error,
  { taskId: string; status: "done" | "cancelled"; outcome: string }
> {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation<CrmTask, Error, { taskId: string; status: "done" | "cancelled"; outcome: string }>({
    mutationFn: ({ taskId, status, outcome }) => client.closeCrmTask(taskId, { status, outcome }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: CRM_DATASET_QUERY_KEY });
      void queryClient.invalidateQueries({ queryKey: CRM_360_QUERY_KEY });
    }
  });
}

/**
 * Expedição (Task 14). A fila, o detalhe e o rastreio leem a MESMA entrada de
 * cache: a etapa que a fila mostra é a etapa que o detalhe mostra, sem janela
 * em que as telas discordem.
 */
export function useFulfillmentDataset(): UseQueryResult<FulfillmentDataset, Error> {
  const client = useApiClient();
  return useQuery<FulfillmentDataset, Error>({
    queryKey: FULFILLMENT_DATASET_QUERY_KEY,
    queryFn: () => client.loadFulfillmentDataset(),
    staleTime: 10_000
  });
}

/**
 * Avanço de etapa da expedição (Task 14). Invalida o dataset de expedição e o
 * de pedidos no sucesso: a entrega registrada aqui e o estado do pedido
 * unificado não podem divergir, e um estado local otimista discordaria do
 * histórico gravado no servidor.
 */
export function useFulfillmentStage(): UseMutationResult<
  FulfillmentTask,
  Error,
  { taskId: string; input: { to: FulfillmentStage } & FulfillmentStageInput }
> {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation<FulfillmentTask, Error, { taskId: string; input: { to: FulfillmentStage } & FulfillmentStageInput }>({
    mutationFn: ({ taskId, input }) => client.advanceFulfillmentStage(taskId, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: FULFILLMENT_DATASET_QUERY_KEY });
      void queryClient.invalidateQueries({ queryKey: ORDERS_DATASET_QUERY_KEY });
    }
  });
}

/** Registro de ocorrência na expedição (atraso/avaria/endereço/ausente/extravio). */
export function useFulfillmentOccurrence(): UseMutationResult<
  FulfillmentTask,
  Error,
  { taskId: string; occurrenceKind: OccurrenceKind; occurrenceNote: string }
> {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation<FulfillmentTask, Error, { taskId: string; occurrenceKind: OccurrenceKind; occurrenceNote: string }>({
    mutationFn: ({ taskId, occurrenceKind, occurrenceNote }) =>
      client.registerFulfillmentOccurrence(taskId, { occurrenceKind, occurrenceNote }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: FULFILLMENT_DATASET_QUERY_KEY });
    }
  });
}

/**
 * Garantia/RMA (Task 15). A fila e o detalhe leem a MESMA entrada de cache: a
 * etapa que a fila mostra é a etapa que o detalhe mostra, sem janela em que as
 * telas discordem.
 */
export function useRmaDataset(): UseQueryResult<RmaDataset, Error> {
  const client = useApiClient();
  return useQuery<RmaDataset, Error>({
    queryKey: RMA_DATASET_QUERY_KEY,
    queryFn: () => client.loadRmaDataset(),
    staleTime: 10_000
  });
}

/**
 * Avanço de etapa do caso de garantia (Task 15). Invalida o dataset de RMA e o
 * do dashboard no sucesso: o KPI "garantias abertas" lê os casos não
 * encerrados, então encerrar um caso aqui muda o indicador, e um estado local
 * otimista discordaria do histórico gravado no servidor.
 */
export function useRmaTransition(): UseMutationResult<
  PlatformRmaCase,
  Error,
  { caseId: string; input: { to: RmaState } & RmaTransitionInput }
> {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation<PlatformRmaCase, Error, { caseId: string; input: { to: RmaState } & RmaTransitionInput }>({
    mutationFn: ({ caseId, input }) => client.advanceRmaState(caseId, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: RMA_DATASET_QUERY_KEY });
      void queryClient.invalidateQueries({ queryKey: DASHBOARD_DATASET_QUERY_KEY });
    }
  });
}

/**
 * Financeiro gerencial (Task 16). Conciliação, margens por pedido/produto,
 * lançamentos e rentabilidade leem a MESMA entrada de cache: o número da
 * rentabilidade é o mesmo que a conciliação e os lançamentos compõem, sem
 * janela em que as telas discordem. `source: "simulator"` no resumo diz que os
 * números vêm do simulador (credencial do adquirente ausente).
 */
export function useFinancialDataset(): UseQueryResult<FinancialDataset, Error> {
  const client = useApiClient();
  return useQuery<FinancialDataset, Error>({
    queryKey: FINANCIAL_DATASET_QUERY_KEY,
    queryFn: () => client.loadFinancialDataset(),
    staleTime: 10_000
  });
}

/**
 * Trilha de auditoria — consultada só pelas telas de cliente (Customer 360),
 * e não junto do dataset do dashboard: ninguém paga o custo de uma rota que
 * a sua tela não usa. `data: null` significa fonte não publicada.
 */
export function useAuditTrail(): UseQueryResult<PlatformAuditEvent[] | null, Error> {
  const client = useApiClient();
  return useQuery<PlatformAuditEvent[] | null, Error>({
    queryKey: AUDIT_TRAIL_QUERY_KEY,
    queryFn: () => client.listAuditEvents(),
    staleTime: 10_000
  });
}

/**
 * Configuração das integrações (Entrega 0, R3–R5). A tela `/configuracoes` lê
 * daqui: estado (simulador/real), o que falta e o flag — nunca o valor de um
 * segredo. `data: null` = rota não publicada neste ambiente (a tela declara a
 * ausência em vez de mostrar "nada configurado").
 */
export function useIntegrations(): UseQueryResult<PlatformIntegrationStatus[] | null, Error> {
  const client = useApiClient();
  return useQuery<PlatformIntegrationStatus[] | null, Error>({
    queryKey: INTEGRATIONS_QUERY_KEY,
    queryFn: () => client.loadIntegrations(),
    staleTime: 10_000
  });
}

// ---------------------------------------------------------------------------
// Usuários da equipe (0058)
// ---------------------------------------------------------------------------
export const STAFF_USERS_QUERY_KEY = ["staff-users"] as const;
export const MY_ACCESS_QUERY_KEY = ["my-access"] as const;

/** Acesso do usuário logado (menu e guarda de rota). `data: null` = rota não publicada → sem restrição de UI. */
export function useMyAccess(): UseQueryResult<StaffUser | null, Error> {
  const client = useApiClient();
  return useQuery<StaffUser | null, Error>({ queryKey: MY_ACCESS_QUERY_KEY, queryFn: () => client.loadMyAccess(), staleTime: 60_000, retry: false });
}

export function useStaffUsers(): UseQueryResult<StaffUser[], Error> {
  const client = useApiClient();
  return useQuery<StaffUser[], Error>({ queryKey: STAFF_USERS_QUERY_KEY, queryFn: () => client.listStaffUsers(), staleTime: 5_000 });
}

export function useStaffUserEvents(id: string | null): UseQueryResult<StaffUserEvent[], Error> {
  const client = useApiClient();
  return useQuery<StaffUserEvent[], Error>({ queryKey: [...STAFF_USERS_QUERY_KEY, id, "events"], queryFn: () => client.listStaffUserEvents(id as string), enabled: id !== null });
}

function useUsersMutation<Input, Output>(run: (client: PlatformApiClient, input: Input) => Promise<Output>): UseMutationResult<Output, Error, Input> {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation<Output, Error, Input>({
    mutationFn: (input) => run(client, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: STAFF_USERS_QUERY_KEY });
      void queryClient.invalidateQueries({ queryKey: MY_ACCESS_QUERY_KEY });
    }
  });
}

export const useStaffUserCreate = () => useUsersMutation<NewStaffUserInput, StaffUser>((client, input) => client.createStaffUser(input));
export const useStaffUserUpdate = () => useUsersMutation<{ id: string; patch: StaffUserPatch }, StaffUser>((client, input) => client.updateStaffUser(input.id, input.patch));
export const useStaffUserDelete = () => useUsersMutation<string, void>((client, id) => client.deleteStaffUser(id));
